import { FormikValues } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { Card } from '@material-ui/core';
import LocalLibraryIcon from '@material-ui/icons/LocalLibrary';

import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useUpdateTraining } from '../Training.hooks';
import { Training, BaseTrainingCardProps } from '../Training.interface';

const getInitialValues = (training?: Training) => {
  return {
    name: training?.name || '',
    description: training?.description || ''
  };
};

const TrainingInfoCard: React.FC<BaseTrainingCardProps> = (
  props: BaseTrainingCardProps
) => {
  const { training, onChange, onUpdate, onError } = props;
  const formikRef = useRef<FormikValues>();
  const {
    training: updatedTraining,
    isLoading: isUpdating,
    error: updateError,
    patch
  } = useUpdateTraining(training.id);

  const [initialValues, setInitialValues] = useState<Partial<Training>>();

  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.Training>(
    training.task
  );

  // set the initial state of the form once loaded
  useEffect(() => {
    setInitialValues(getInitialValues(training));
  }, [training]);

  // notify parent when a training record is updated
  useEffect(() => {
    if (updatedTraining) {
      onChange?.(updatedTraining);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTraining]);

  useEffect(() => {
    if (updateError) {
      onError?.(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError]);

  return (
    <Card>
      <CardTitle>
        <LocalLibraryIcon />
        Training Info
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={initialValues}
          validationSchema={Yup.object({
            name: Yup.string().required('Required')
          })}
          isSubmitting={isUpdating}
          error={updateError}
          onSubmit={(training: Partial<Training>) => {
            console.log('onSubmit', training);
            patch(training);
            onChange?.(training);
          }}
        >
          <InlineForm>
            <InlineTextField
              name='name'
              label='Training Name'
              placeholder='Provide a name for the training.'
              disabled={isFieldDisabled('name')}
            />
            <InlineTextField
              multiline
              name='description'
              label='Description'
              placeholder='Provide an overview of the training and what will be covered in the lesson.'
              disabled={isFieldDisabled('description')}
            />
          </InlineForm>
        </InlineFormik>
      </CardContent>
    </Card>
  );
};

export default TrainingInfoCard;
